<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" :items="items" class="mc-page-three" />
      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end d-flex">
          <McButtonBack href="/setting/engine-off" class="me-2" />
        </div>
      </div>
    </div>

    <FormulateForm ref="form" v-model="form" :errors="formErrors" class="mc-form_engine-off">
      <b-card>
        <b-row>
          <b-col md="6">
            <h5 class="card-title">
              {{ $t('global.object.licensePlate') }}
            </h5>
            <b-skeleton-wrapper :loading="isLoading">
              <template #loading>
                <b-skeleton width="20%" height="26px" />
              </template>
              <p class="teal-300">
                {{ licensePlate }}
              </p>
            </b-skeleton-wrapper>
          </b-col>
          <b-col md="6" class="text-end">
            <div class="d-flex align-items-center justify-content-end">
              <h5 class="card-title me-2">
                {{ $t('setting.cutOffAutomation.automationStatus') }}
              </h5>
              <McToggle
                v-model="form.status"
                placement="left"
                :label="form.status ? $t('global.active') : $t('global.nonActive3')"
                :disabled="!hasScheduled || isLoading"
                @change="onChangeStatus()"
              />
            </div>
          </b-col>
        </b-row>
        <hr />
        <b-row>
          <h5 class="card-title">
            {{ $t('global.object.operationTime') }}
          </h5>
          <p class="teal-300">
            {{ $t('setting.cutOffAutomation.setOperationTime') }}
          </p>
        </b-row>
        <b-row>
          <b-col md="6">
            <McAlert
              class="mb-3 mt-3"
              variant="info"
              :text="$t('setting.cutOffAutomation.maxTime')"
              withIcon
              :withGap="false"
            />
          </b-col>
        </b-row>
        <b-row class="row-class">
          <b-col v-for="(day, dayIndex) in days" :key="dayIndex" md="2">
            <b-card class="text-center mc-card-engine-off">
              <b-card-header class="mc-card-engine-off__header">
                {{ day.name }}
              </b-card-header>

              <div v-if="day.schedules.length === 0" class="add-schedule-wrapper">
                <McButton
                  :id="`popover-button-event-${dayIndex}`"
                  variant="secondary"
                  :text="$t('global.action.addSchedule')"
                  icon="icon-outline_plus"
                  :borderNone="true"
                  :disabled="isLoading"
                  @click="handleAddScheduleClick(dayIndex)"
                />
                <component
                  :is="'b-popover'"
                  ref="popover"
                  custom-class="popover-schedule-wrapper"
                  :target="`popover-button-event-${dayIndex}`"
                  triggers="click"
                  :show="activePopovers.add === dayIndex"
                  :placement="dayIndex > 5 ? 'leftbottom' : 'bottom'"
                >
                  <b-row>
                    <b-col md="6">
                      <FormulateInput name="startTime" type="time" :value="form.startTime" />
                    </b-col>
                    <b-col md="6">
                      <FormulateInput name="endTime" type="time" :value="form.endTime" />
                    </b-col>
                  </b-row>
                  <div class="d-flex flex-row-reverse mt-2">
                    <McButton
                      variant="primary"
                      :text="$t('global.action.save')"
                      size="sm"
                      @click="onSave(dayIndex)"
                    />
                    <McButton
                      variant="secondary"
                      :text="$t('global.action.cancel')"
                      size="sm"
                      class="me-2"
                      @click="onClose()"
                    />
                  </div>
                </component>
              </div>

              <b-list-group v-else flush>
                <b-list-group-item
                  v-for="(schedule, scheduleIndex) in day.schedules"
                  :key="scheduleIndex"
                >
                  <div class="d-flex justify-content-between detail-item">
                    {{ schedule.startTime }} - {{ schedule.endTime }}
                    <div class="float-end detail-item__action">
                      <i
                        :id="`popover-button-event-edit-${dayIndex}-${scheduleIndex}`"
                        class="mc-fa icon-outline_edit me-3"
                        :class="{ disabled: day.popoverEditStates.includes(true) }"
                        @click="handleEditClick(dayIndex, scheduleIndex)"
                      ></i>
                      <i
                        class="mc-fa icon-outline_trash"
                        :class="{ disabled: day.popoverEditStates.includes(true) }"
                        @click="onDelete(dayIndex, scheduleIndex)"
                      ></i>
                    </div>
                    <component
                      :is="'b-popover'"
                      ref="popoverEdit"
                      custom-class="popover-schedule-wrapper"
                      :target="`popover-button-event-edit-${dayIndex}-${scheduleIndex}`"
                      triggers="click"
                      :show.sync="day.popoverEditStates[scheduleIndex]"
                      :placement="dayIndex > 5 ? 'leftbottom' : 'bottom'"
                    >
                      <b-row>
                        <b-col md="6">
                          <FormulateInput name="startTime" type="time" :value="form.startTime" />
                        </b-col>
                        <b-col md="6">
                          <FormulateInput name="endTime" type="time" :value="form.endTime" />
                        </b-col>
                      </b-row>
                      <div class="d-flex flex-row-reverse mt-2">
                        <McButton
                          variant="primary"
                          :text="$t('global.action.save')"
                          size="sm"
                          @click="onSaveEdit(dayIndex, scheduleIndex)"
                        />
                        <McButton
                          variant="secondary"
                          :text="$t('global.action.cancel')"
                          size="sm"
                          class="me-2"
                          @click="onClose()"
                        />
                      </div>
                    </component>
                  </div>
                </b-list-group-item>
              </b-list-group>

              <template v-if="day.schedules.length > 0" #footer>
                <div
                  class="mc-button-wrapper"
                  @mouseenter="showTooltip(dayIndex)"
                  @mouseleave="hideTooltip(dayIndex)"
                >
                  <a
                    :id="`popover-button-event-footer-${dayIndex}`"
                    class="mc-primary"
                    :class="{ disabled: day.schedules.length >= 3 || isLoading }"
                    @click="handleFooterClick(dayIndex)"
                  >
                    <i class="mc-fa icon-outline_plus"></i>
                    <span>{{ $t('global.action.addSchedule') }}</span>
                  </a>
                  <div
                    v-if="tooltipVisibleMap[dayIndex] && day.schedules.length >= 3"
                    class="tooltip"
                    :data-position="dayIndex > 5 ? 'leftbottom' : 'bottom'"
                  >
                    {{ $t('setting.cutOffAutomation.message.reachMaxTime') }}
                  </div>
                </div>

                <component
                  :is="'b-popover'"
                  ref="popoverFooter"
                  custom-class="popover-schedule-wrapper"
                  :target="`popover-button-event-footer-${dayIndex}`"
                  triggers="click"
                  :show="activePopovers.footer === dayIndex"
                  :placement="dayIndex > 5 ? 'leftbottom' : 'bottom'"
                >
                  <b-row>
                    <b-col md="6">
                      <FormulateInput name="startTime" type="time" :value="form.startTime" />
                    </b-col>
                    <b-col md="6">
                      <FormulateInput name="endTime" type="time" :value="form.endTime" />
                    </b-col>
                  </b-row>
                  <div class="d-flex flex-row-reverse mt-2">
                    <McButton
                      variant="primary"
                      :text="$t('global.action.save')"
                      size="sm"
                      @click="onSave(dayIndex)"
                    />
                    <McButton
                      variant="secondary"
                      :text="$t('global.action.cancel')"
                      size="sm"
                      class="me-2"
                      @click="onClose()"
                    />
                  </div>
                </component>
              </template>
            </b-card>
          </b-col>
        </b-row>
      </b-card>
    </FormulateForm>
    <b-modal
      id="mc-modal-clear_scheduled"
      ref="mc-modal-clear_scheduled"
      class="mc-modal"
      :title="$t('global.delete')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <div class="card-body p-2">
          <p>
            {{ $t('setting.cutOffAutomation.message.confirmDelete') }}
            <strong>{{ days[deletingDayIndex]?.name }}</strong>
            <strong>
              {{ days[deletingDayIndex]?.schedules[deletingScheduleIndex]?.startTime }} -
              {{ days[deletingDayIndex]?.schedules[deletingScheduleIndex]?.endTime }}
            </strong>
            ?
          </p>
        </div>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            :text="$t('global.action.cancel')"
            class="me-2"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.delete')" @click="handleConfirm" />
        </div>
      </template>
    </b-modal>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import McButtonBack from '@/components/McButtonBack'
import McToggle from '@/components/McToggle'
import McAlert from '@/components/McAlert'
import McButton from '@/components/McButton'
import {
  GET_CUT_OFF_ENGINE,
  UPSERT_CUT_OFF_ENGINE,
} from '@/services/api/setting/engineOff/engineOff'

export default {
  page: {
    title: 'Engine Off Automation',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McToggle,
    McAlert,
    McButton,
  },
  data() {
    return {
      hasScheduled: false,
      form: {},
      formErrors: {},
      daysData: [
        { schedules: [], popoverEditStates: [] },
        { schedules: [], popoverEditStates: [] },
        { schedules: [], popoverEditStates: [] },
        { schedules: [], popoverEditStates: [] },
        { schedules: [], popoverEditStates: [] },
        { schedules: [], popoverEditStates: [] },
        { schedules: [], popoverEditStates: [] },
      ],
      popoverShow: false,
      popoverShowFooter: false,
      activePopovers: {
        add: null,
        footer: null,
      },
      deletingDayIndex: null,
      deletingScheduleIndex: null,
      tooltipVisibleMap: {},
      licensePlate: null,
      isLoading: true,
    }
  },
  computed: {
    title() {
      return this.$t('setting.cutOffAutomation.title.engineOffSchedule')
    },
    items() {
      return [
        {
          text: this.$t('setting.list.fleetGroup.setting'),
          to: '/setting',
        },
        {
          text: this.$t('setting.homePage.cutOffAutomation'),
          to: '/setting/engine-off',
        },
        {
          text: this.$t('setting.cutOffAutomation.title.engineOffSchedule'),
        },
      ]
    },
    days() {
      const dayNames = [
        this.$t('global.days.monday'),
        this.$t('global.days.tuesday'),
        this.$t('global.days.wednesday'),
        this.$t('global.days.thursday'),
        this.$t('global.days.friday'),
        this.$t('global.days.saturday'),
        this.$t('global.days.sunday'),
      ]

      return this.daysData.map((day, index) => ({
        name: dayNames[index],
        schedules: day.schedules,
        popoverEditStates: day.popoverEditStates,
      }))
    },
  },
  mounted() {
    this.fetchCutOffEngineDetail()
  },
  methods: {
    isCardAtRightEdge(dayIndex) {
      const card = document.querySelector(`#popover-button-event-footer-${dayIndex}`)
      if (card) {
        const rect = card.getBoundingClientRect()
        const screenWidth = window.innerWidth
        const rightEdgeThreshold = screenWidth - 200 // Adjust threshold as needed
        return rect.right > rightEdgeThreshold
      }
      return false
    },
    showTooltip(dayIndex) {
      if (this.days[dayIndex].schedules.length >= 3) {
        this.$set(this.tooltipVisibleMap, dayIndex, true)
      }
    },
    hideTooltip(dayIndex) {
      this.$set(this.tooltipVisibleMap, dayIndex, false)
    },
    validateTimeRange() {
      const start = new Date(`2000/01/01 ${this.form.startTime}`)
      const end = new Date(`2000/01/01 ${this.form.endTime}`)

      if (start >= end) {
        this.formErrors = {
          endTime: this.$t('setting.cutOffAutomation.error.endTimeLaterThanStartTime'),
        }
        return false
      }
      return true
    },
    validateTimeOverlap(dayIndex, startTime, endTime, editingIndex = null) {
      const schedules = this.days[dayIndex].schedules
      const newStart = new Date(`2000/01/01 ${startTime}`).getTime()
      const newEnd = new Date(`2000/01/01 ${endTime}`).getTime()

      for (let i = 0; i < schedules.length; i++) {
        if (i === editingIndex) continue

        const existingStart = new Date(`2000/01/01 ${schedules[i].startTime}`).getTime()
        const existingEnd = new Date(`2000/01/01 ${schedules[i].endTime}`).getTime()

        if (
          (newStart >= existingStart && newEnd <= existingEnd) ||
          newStart === existingEnd ||
          newEnd === existingStart ||
          (newStart < existingEnd && newEnd > existingStart)
        ) {
          this.formErrors = {
            startTime: this.$t('setting.cutOffAutomation.error.scheduleAlreadyExist'),
            endTime: this.$t('setting.cutOffAutomation.error.scheduleAlreadyExist'),
          }
          return false
        }
      }
      return true
    },
    handleAddScheduleClick(dayIndex) {
      this.formErrors = {}
      this.activePopovers.footer = null
      this.days.forEach((day) => {
        day.popoverEditStates = day.schedules.map(() => false)
      })
      this.activePopovers.add = dayIndex
    },
    handleFooterClick(dayIndex) {
      this.formErrors = {}
      this.activePopovers.add = null
      this.days.forEach((day) => {
        day.popoverEditStates = day.schedules.map(() => false)
      })
      this.activePopovers.footer = dayIndex
    },
    handleEditClick(dayIndex, scheduleIndex) {
      this.formErrors = {}
      this.activePopovers.add = null
      this.activePopovers.footer = null
      this.days.forEach((day) => {
        day.popoverEditStates = day.schedules.map(() => false)
      })
      this.onEdit(dayIndex, scheduleIndex)
    },
    onClose() {
      this.formErrors = {}
      this.activePopovers.add = null
      this.activePopovers.footer = null
      this.days.forEach((day) => {
        day.popoverEditStates = day.schedules.map(() => false)
      })
    },
    async onSave(dayIndex) {
      this.formErrors = {}

      if (!this.form.startTime) {
        this.formErrors.startTime = this.$t('global.validation.required')
      }

      if (!this.form.endTime) {
        this.formErrors.endTime = this.$t('global.validation.required')
      }

      if (Object.keys(this.formErrors).length > 0) {
        return
      }

      if (!this.validateTimeRange()) return
      if (!this.validateTimeOverlap(dayIndex, this.form.startTime, this.form.endTime)) return

      const dayMap = {
        0: 'mon',
        1: 'tue',
        2: 'wed',
        3: 'thu',
        4: 'fri',
        5: 'sat',
        6: 'sun',
      }

      const day = this.days[dayIndex]

      try {
        if (day.schedules.length < 3) {
          const totalSchedules = this.days.reduce((total, day) => total + day.schedules.length, 0)
          const isFirstSchedule = totalSchedules === 0

          day.schedules.push({
            startTime: this.form.startTime,
            endTime: this.form.endTime,
          })

          const operationTimeSchedule = {
            [dayMap[dayIndex]]: day.schedules.map((schedule) => ({
              startTime: schedule.startTime + ':00',
              endTime: schedule.endTime + ':00',
            })),
          }

          // If first schedule, set status to true
          if (isFirstSchedule) {
            this.form.status = true
            this.automationStatus = true
          }

          await this.updateCutOffEngine({
            operationTimeSchedule: operationTimeSchedule,
            isActive: isFirstSchedule ? true : this.automationStatus,
          })

          await this.fetchCutOffEngineDetail()

          this.activePopovers.add = null
          this.activePopovers.footer = null
          const currentStatus = this.form.status
          this.form = {
            status: currentStatus,
          }
          this.$mcToast.success({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                { tag: 'span', text: this.$t('setting.cutOffAutomation.message.addSuccess') },
                { tag: 'strong', text: day.name, class: '__status' },
              ],
            },
          })
        }
      } catch (error) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              { tag: 'span', text: this.$t('setting.cutOffAutomation.message.addFailed') },
              { tag: 'strong', text: day.name, class: '__status' },
            ],
          },
        })
      }
    },
    async handleConfirm() {
      const deletedDay = this.days[this.deletingDayIndex].name
      const dayMap = {
        0: 'mon',
        1: 'tue',
        2: 'wed',
        3: 'thu',
        4: 'fri',
        5: 'sat',
        6: 'sun',
      }

      try {
        this.days[this.deletingDayIndex].schedules.splice(this.deletingScheduleIndex, 1)

        const totalSchedules = this.days.reduce((total, day) => total + day.schedules.length, 0)
        const isLastSchedule = totalSchedules === 0

        const operationTimeSchedule = {
          [dayMap[this.deletingDayIndex]]: this.days[this.deletingDayIndex].schedules.map(
            (schedule) => ({
              startTime: schedule.startTime + ':00',
              endTime: schedule.endTime + ':00',
            })
          ),
        }

        this.$refs['mc-modal-clear_scheduled'].hide()

        await this.updateCutOffEngine({
          operationTimeSchedule: operationTimeSchedule,
          isActive: isLastSchedule ? false : this.automationStatus,
        })

        await this.fetchCutOffEngineDetail()

        this.$mcToast.success(
          `${this.$t('setting.cutOffAutomation.message.deleteSuccess')} ${deletedDay}!`
        )
      } catch (error) {
        this.$mcToast.error(
          `${this.$t('setting.cutOffAutomation.message.deleteFailed')} ${deletedDay}!`
        )
      }
    },
    onDelete(dayIndex, scheduleIndex) {
      this.$refs['mc-modal-clear_scheduled'].show()
      this.deletingDayIndex = dayIndex
      this.deletingScheduleIndex = scheduleIndex
    },
    onEdit(dayIndex, scheduleIndex) {
      const schedule = this.days[dayIndex].schedules[scheduleIndex]
      const currentStatus = this.form.status
      this.form = {
        status: currentStatus,
        startTime: schedule.startTime,
        endTime: schedule.endTime,
      }
      this.days[dayIndex].popoverEditStates = this.days[dayIndex].schedules.map(
        (_, i) => i === scheduleIndex
      )
    },
    async onSaveEdit(dayIndex, scheduleIndex) {
      this.formErrors = {}

      if (!this.form.startTime) {
        this.formErrors.startTime = this.$t('global.validation.required')
      }

      if (!this.form.endTime) {
        this.formErrors.endTime = this.$t('global.validation.required')
      }

      if (Object.keys(this.formErrors).length > 0) {
        return
      }

      if (!this.validateTimeRange()) return

      const originalSchedule = { ...this.days[dayIndex].schedules[scheduleIndex] }
      const tempSchedules = [...this.days[dayIndex].schedules]
      tempSchedules.splice(scheduleIndex, 1)

      if (
        !this.validateTimeOverlap(dayIndex, this.form.startTime, this.form.endTime, scheduleIndex)
      ) {
        this.days[dayIndex].schedules[scheduleIndex] = originalSchedule
        return
      }

      const dayMap = {
        0: 'mon',
        1: 'tue',
        2: 'wed',
        3: 'thu',
        4: 'fri',
        5: 'sat',
        6: 'sun',
      }

      try {
        this.days[dayIndex].schedules[scheduleIndex] = {
          startTime: this.form.startTime,
          endTime: this.form.endTime,
        }

        const operationTimeSchedule = {
          [dayMap[dayIndex]]: this.days[dayIndex].schedules.map((schedule) => ({
            startTime: schedule.startTime + ':00',
            endTime: schedule.endTime + ':00',
          })),
        }

        await this.updateCutOffEngine({
          operationTimeSchedule: operationTimeSchedule,
          isActive: this.automationStatus,
        })

        await this.fetchCutOffEngineDetail()

        this.days[dayIndex].popoverEditStates[scheduleIndex] = false
        const currentStatus = this.form.status
        this.form = {
          status: currentStatus,
        }
        this.$mcToast.success({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              { tag: 'span', text: this.$t('setting.cutOffAutomation.message.updateSuccess') },
              { tag: 'strong', text: this.days[dayIndex].name, class: '__status' },
            ],
          },
        })
      } catch (error) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              { tag: 'span', text: this.$t('setting.cutOffAutomation.message.updateFailed') },
              { tag: 'strong', text: this.days[dayIndex].name, class: '__status' },
            ],
          },
        })
      }
    },
    async onChangeStatus() {
      const statusKey = this.form.status ? 'global.active' : 'global.nonActive3'
      const status = this.$t(statusKey)

      try {
        const response = await this.updateCutOffEngine({ isActive: this.form.status })

        if (!response?.upsertCutOffEngine?.isSuccessful) throw new Error()

        this.form.status = response.upsertCutOffEngine.upsertCutOffEngine.isActive
        this.automationStatus = response.upsertCutOffEngine.upsertCutOffEngine.isActive
        this.$mcToast.success({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              { tag: 'span', text: this.$t('setting.cutOffAutomation.message.statusSuccess') },
              { tag: 'strong', text: status, class: '__status' },
            ],
          },
        })
      } catch {
        this.form.status = !this.form.status
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              { tag: 'span', text: this.$t('setting.cutOffAutomation.message.statusFailed') },
              { tag: 'strong', text: status, class: '__status' },
            ],
          },
        })
      }
    },
    async updateCutOffEngine(payload) {
      const response = await this.$apollo.mutate({
        mutation: UPSERT_CUT_OFF_ENGINE,
        variables: {
          input: {
            vehicleId: parseInt(this.$route.params.id),
            ...payload,
          },
        },
      })
      return response.data
    },
    async fetchCutOffEngineDetail() {
      try {
        const vehicleId = parseInt(this.$route.params.id)
        const response = await this.$apollo.query({
          query: GET_CUT_OFF_ENGINE,
          fetchPolicy: 'no-cache',
          variables: {
            filter: {
              vehicleId: vehicleId,
            },
          },
        })

        if (!response.data.cutOffEngine.isSuccessful) {
          this.$mcToast.warning(response.data.cutOffEngine.message)
          return
        }

        const detail = response.data.cutOffEngine.cutOffEngine
        this.form = {
          status: detail.isActive,
        }

        this.automationStatus = detail.isActive

        const daysMap = {
          mon: 0,
          tue: 1,
          wed: 2,
          thu: 3,
          fri: 4,
          sat: 5,
          sun: 6,
        }

        let hasAnySchedule = false
        Object.entries(detail.operationTime).forEach(([day, schedules]) => {
          if (day in daysMap) {
            const dayIndex = daysMap[day]
            this.daysData[dayIndex].schedules = schedules.map((schedule) => ({
              startTime: schedule.startTime.substring(0, 5),
              endTime: schedule.endTime.substring(0, 5),
            }))
            if (schedules.length > 0) {
              hasAnySchedule = true
            }
          }
        })

        this.hasScheduled = hasAnySchedule

        this.licensePlate = detail.vehicle.licensePlate

        this.days.forEach((day) => {
          day.popoverEditStates = day.schedules.map(() => false)
        })
        this.isLoading = false
      } catch (error) {
        this.$mcToast.error(this.$t('setting.cutOffAutomation.message.fetchDetailFailed'))
      }
    },
  },
}
</script>
